/* eslint-disable consistent-return */
import { cloneDeep, isEmpty, map, set, filter, isEqual } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';
import turf from '@turf/turf';

import * as annotationOps from './annotation-operations';
import * as videoOps from '../Videos/video-operations';
import * as videoCalculate from '../Videos/videos-calculations';

import methodPromise from '../../modules/method-promise-wrapper';
import handleErrorInClient from '../../modules/handle-error-in-client';

/**
 * Deletes all the automatic annotations from a video.
 *
 * @async
 * @param {string} videoId - The video id to delete the automatic annotations from.
 * @returns {object} An object which contains the status of the operation.
 * @throws Error if something went wrong.
 */
export const deleteAutomaticAnnotations = async videoId => {
  try {
    methodPromise('annotations.deleteAutomatic', videoId);
  } catch (error) {
    handleErrorInClient(error);
  }
};

/**
 * Imports annotations contained in an object and adds them to a video.
 *
 * @async
 * @param {object} arg - The arguments object.
 * @param {object} arg.variableWithObjects - The variable with the project content to import.
 * @param {object} arg.videoId - The video to add the annotations to.
 * @param {object} arg.spatialSync - Flag to synchronize annotations by location or time.
 * @throws Error if seconds is empty.
 */
export const importAnnotations = async ({
  variableWithObjects,
  videoId,
  spatialSync,
  syncTimeGPS = false,
  annotationsProps = []
}) => {
  const { annotations = [] } = variableWithObjects;
  const manyAnnotations = isEmpty(annotations) ? variableWithObjects : annotations;
  const orderManyAnnotations = manyAnnotations.sort(
    (first, second) => first.geoJSON.properties.videoTime - second.geoJSON.properties.videoTime
  );

  const calculateTimeAnnotation = (video, lng, lat) => {
    const videoTrack = videoOps.getTrack(video);
    const { coordinates } = videoTrack.geometry;
    const turfLine = turf.lineString(coordinates);
    const newPointTurf = turf.point([lng, lat]);
    const proyectedPointTrackVideo = turf.nearestPointOnLine(turfLine, newPointTurf);
    const time = videoCalculate.calculateTimeVideoLine(
      video,
      proyectedPointTrackVideo,
      annotationsProps
    );
    return time;
  };

  const setPropertiesNewAnnotations = (annotation, coordinates, idVideo, newTime) => {
    const newAnnotation = cloneDeep(annotation);
    delete newAnnotation._id;
    set(newAnnotation, ['geoJSON', 'geometry', 'coordinates'], coordinates);
    set(newAnnotation, ['geoJSON', 'properties', 'video'], idVideo);
    set(newAnnotation, ['geoJSON', 'properties', 'videoTime'], newTime);
    return newAnnotation;
  };

  try {
    const video = await methodPromise('videos.findOne', videoId);
    const isVideoLine = video.coordinatesDataType !== 'point';
    const newAnnotations = map(orderManyAnnotations, annotation => {
      const geoJson = annotationOps.getGeoJSON(annotation);
      const { coordinates } = geoJson.geometry;
      if (spatialSync && isVideoLine) {
        const lng = coordinates[0];
        const lat = coordinates[1];
        const newTime = calculateTimeAnnotation(video, lng, lat);
        const newAnnotation = setPropertiesNewAnnotations(
          annotation,
          coordinates,
          videoId,
          newTime
        );
        return newAnnotation;
      }
      if (syncTimeGPS) {
        const newTime = videoCalculate.calculateTimeByGPSAnnotation(
          video,
          annotation,
          annotationsProps
        );
        if (newTime > 0) {
          const newAnnotation = setPropertiesNewAnnotations(
            annotation,
            coordinates,
            videoId,
            newTime * 1000
          );
          return newAnnotation;
        }
      } else {
        const videoTime = annotationOps.getVideoTime(annotation);
        const timeChecked = videoCalculate.checkTimeAnnotation(videoTime, annotationsProps);
        const newAnnotation = setPropertiesNewAnnotations(
          annotation,
          coordinates,
          videoId,
          timeChecked
        );
        return newAnnotation;
      }
      return 'none';
    });
    const noImportAnnotations = filter(newAnnotations, element => element === 'none');
    methodPromise('annotations.insertMany', newAnnotations);
    return { newAnnotations, video, countNoImported: noImportAnnotations.length };
  } catch (error) {
    Bert.alert(error.message, 'danger');
  }
};

/**
 * Saves the entities detected in the video as annotations.
 *
 * @async
 * @param {string} videoId - The video id to insert the annotations in.
 * @param {object[]} detectedEntities - An array with the elements to create the annotations from.
 * @param {object} template - The template to use for the annotations.
 * @param {object[]} featureArray - The array with all the coordinates of the track as feature
 * points.
 * @returns {string[]} An object with the status of the request, its progress and, in case it has
 * finished, an array with the ids of the new annotations inserted in the database.
 * @throws Error if something went wrong.
 */
export const saveVideoEntities = async (videoId, detectedEntities, template, featureArray) => {
  try {
    return await methodPromise(
      'annotations.saveVideoEntities',
      videoId,
      detectedEntities,
      template,
      featureArray
    );
  } catch (error) {
    handleErrorInClient(error);
  }
};

/**
 * Import annotations in several videos of a project.
 *
 * @param {object[]} videos - The videos to insert annotations.
 * @param {object[]} annotations - The annotations array.
 * @param {number} distanceFilter - The filter to distance.
 * @param {boolean} isTimeGPS - If import with time GPS filter active.
 * @param {object} templateSelect - Template selected.
 * @param {object} tagsSelect - Tags selected.
 * @returns {object} - Object to return.
 */

export const importAnnotationsToVideosProject = (
  videos,
  annotations,
  distanceFilter,
  isTimeGPS,
  templateSelect,
  tagsSelect
) => {
  const arrayToReturn = [];
  map(videos, video => {
    const track = videoOps.getTrack(video);
    const line = turf.lineString(track.geometry.coordinates);

    const objectToReturn = {
      video,
      numAnnotations: 0,
      annotationsImport: []
    };
    const arrayAnotationsToImport = [];

    if (templateSelect !== 'none') {
      const templatesVideos = videoOps.getAnnotationTemplates(video);
      map(templatesVideos, template => {
        const nameTemplateVideo = template.name;
        if (templateSelect === nameTemplateVideo) objectToReturn.checkTemplate = true;
        else objectToReturn.checkTemplate = false;
      });
    }

    if (!isEmpty(tagsSelect)) {
      const tagsVideos = videoOps.getTags(video);
      if (!isEmpty(tagsVideos)) {
        let check = false;
        map(tagsVideos, tag => {
          const tagVideoId = tag.id;
          map(tagsSelect, tagSelect => {
            if (isEqual(tagVideoId, tagSelect.id)) {
              objectToReturn.checkTags = true;
              check = true;
            }
          });
        });
        if (!check) objectToReturn.checkTags = false;
      } else objectToReturn.checkTags = false;
    }

    map(annotations, annotation => {
      const coords = turf.getCoord(annotationOps.getGeoJSON(annotation));
      const point = turf.point(coords);
      const distance = turf.pointToLineDistance(point, line);
      if (distance <= distanceFilter) {
        const countAnnotations = objectToReturn.numAnnotations;
        arrayAnotationsToImport.push(annotation);
        objectToReturn.numAnnotations = countAnnotations + 1;
        objectToReturn.annotationsImport = arrayAnotationsToImport;
      }
      if (isTimeGPS) {
        const timeGPSAnnotation = annotation.timeGPS;
        const dateGPSReference = new Date('1980-01-06T00:00:00Z');
        const dateGPSRecordingVideo = new Date(videoOps.getTimeGPS(video));
        const startTimeGPSRecording = dateGPSRecordingVideo.getTime() - dateGPSReference.getTime();
        const finalTimeGPSRecording = videoCalculate.getFinalGPSTimeVideo(
          video,
          'format-milliseconds'
        );
        if (
          startTimeGPSRecording - timeGPSAnnotation < 0 &&
          finalTimeGPSRecording - timeGPSAnnotation > 0
        ) {
          objectToReturn.checkTimeGPS = true;
        } else objectToReturn.checkTimeGPS = false;
      }
    });
    let objectCheckFilters = true;
    if (objectToReturn.numAnnotations <= 0) objectCheckFilters = false;
    if (isTimeGPS && !objectToReturn.checkTimeGPS) objectCheckFilters = false;
    if (!isEmpty(tagsSelect) && !objectToReturn.checkTags) objectCheckFilters = false;
    if (templateSelect !== 'none' && !objectToReturn.checkTemplate) objectCheckFilters = false;

    if (objectCheckFilters) arrayToReturn.push(objectToReturn);
  });
  return arrayToReturn;
};

export default {
  deleteAutomaticAnnotations,
  importAnnotations,
  saveVideoEntities,
  importAnnotationsToVideosProject
};
